import axios from "axios";

const GEMINI_KEY = process.env.REACT_APP_GEMINI_KEY;
const GEMINI_MODEL = process.env.REACT_APP_GEMINI_MODEL || "gemini-2.0-flash";

const geminiApi = axios.create({
  baseURL: `https://generativelanguage.googleapis.com/v1beta/models/${GEMINI_MODEL}:generateContent`,
  params: { key: GEMINI_KEY },
  headers: { "Content-Type": "application/json" },
});

// Strict rule injected into every prompt
const STRICT_RULE = `
STRICT RULES (must follow):
- Use ONLY the data provided. Do not assume or invent any values.
- Do NOT add currency symbols (no $, ₹, USD, BHD, etc.).
- Do NOT add units unless the data itself contains them.
- Do NOT include generic advice unrelated to the actual data.
- Keep every insight short, direct, and data-specific.
- Return valid JSON only — no markdown, no code fences, no explanation outside the JSON.
`;

/**
 * Generic Gemini call — returns parsed JSON.
 */
const callGemini = async (prompt) => {
  const response = await geminiApi.post("", {
    contents: [{ parts: [{ text: prompt }] }],
    generationConfig: { temperature: 0.2, maxOutputTokens: 2048 },
  });

  const raw = response.data?.candidates?.[0]?.content?.parts?.[0]?.text || "";
  const finishReason = response.data?.candidates?.[0]?.finishReason;
  if (finishReason === "MAX_TOKENS") {
    console.warn("Gemini response was truncated (MAX_TOKENS). Increase maxOutputTokens or shorten the prompt.");
  }
  const cleaned = raw.replace(/```json\n?/g, "").replace(/```\n?/g, "").trim();
  return JSON.parse(cleaned);
};

/**
 * Analyze school overview data → 3–5 insights.
 * Returns: [{ type, text, reasoning, action, path }]
 */
export const analyzeSchoolData = async ({ students, revenue, feePending, avgScore, staffStrength, performanceTrend }) => {
  const prompt = `
${STRICT_RULE}

You are a school analytics AI. Analyze this data and return 3-5 insights.

Data:
- Total Students: ${students}
- Revenue MTD: ${revenue}
- Fee Pending: ${feePending}
- Avg Academic Score: ${avgScore}%
- Staff Strength: ${staffStrength}
- Performance Trend (last 6 months): ${JSON.stringify(performanceTrend)}

Return a JSON array:
[
  {
    "type": "WARNING" | "DANGER" | "INFO" | "SUCCESS",
    "text": "One sentence. Use only the numbers from the data above.",
    "reasoning": "2 sentences max. Reference only what is in the data.",
    "action": "Short action label",
    "path": "/intelligence/ai-center"
  }
]
`;

  try {
    return await callGemini(prompt);
  } catch (err) {
    console.error("Gemini analyzeSchoolData error:", err);
    return [];
  }
};

/**
 * Analyze finance dashboard data → 3–5 finance-specific insights.
 * Returns: [{ type, text, reasoning, action }]
 */
export const analyzeFinanceData = async (financeData) => {
  const prompt = `
${STRICT_RULE}

You are a school finance analytics AI. Analyze this finance data and return 3-5 insights.

Data:
${JSON.stringify(financeData, null, 2)}

Return a JSON array:
[
  {
    "type": "WARNING" | "DANGER" | "INFO" | "SUCCESS",
    "text": "One sentence. Use exact numbers from the data. No currency symbols.",
    "reasoning": "1-2 sentences. Only reference the data provided.",
    "action": "Short action label"
  }
]
`;

  try {
    return await callGemini(prompt);
  } catch (err) {
    console.error("Gemini analyzeFinanceData error:", err);
    return [];
  }
};

/**
 * Ask AI a natural language question about school data.
 * Returns: { answer, tableData?, suggestion? }
 */
export const askAI = async (question, contextData = {}) => {
  const prompt = `
${STRICT_RULE}

You are an AI assistant for a school ERP called Office Flow.
User question: "${question}"
Available data: ${JSON.stringify(contextData)}

Return JSON:
{
  "answer": "Direct answer in 1-3 sentences. No currency symbols. Only use what is in the data.",
  "tableData": null,
  "suggestion": "One follow-up suggestion if relevant, otherwise null"
}

If the question involves listing data and context data is available, set "tableData" to an array of objects.
`;

  try {
    return await callGemini(prompt);
  } catch (err) {
    console.error("Gemini askAI error:", err);
    return { answer: "Could not process the request. Please try again.", tableData: null, suggestion: null };
  }
};

/**
 * Generate smart insights across categories.
 * Returns: [{ category, title, description, impact, trend, value, recommendation }]
 */
export const generateSmartInsights = async (data) => {
  const prompt = `
${STRICT_RULE}

You are a school analytics AI. Generate 6 smart insights from this data.

Data:
${JSON.stringify(data, null, 2)}

Return a JSON array:
[
  {
    "category": "Academic" | "Finance" | "Attendance" | "HR",
    "title": "Short title based on actual data values",
    "description": "2 sentences using only the provided numbers",
    "impact": "High" | "Medium" | "Low",
    "trend": "up" | "down" | "stable",
    "value": "A number or percentage from the data (no currency symbol)",
    "recommendation": "One specific action based on the data"
  }
]
`;

  try {
    return await callGemini(prompt);
  } catch (err) {
    console.error("Gemini generateSmartInsights error:", err);
    return [];
  }
};

/**
 * Generate predictions for a given category.
 * Returns: { forecast, summary, confidenceAvg, impact }
 */
export const generatePredictions = async (category, historicalData) => {
  const prompt = `
${STRICT_RULE}

You are a forecasting AI for a school ERP.

Category: ${category}
Historical data (last 6 months): ${JSON.stringify(historicalData)}

Generate a 3-month forecast based strictly on the trend in the historical data.

Return JSON:
{
  "forecast": [
    { "month": "MonthName", "actual": null, "predicted": <number>, "confidence": <0-100> },
    { "month": "MonthName", "actual": null, "predicted": <number>, "confidence": <0-100> },
    { "month": "MonthName", "actual": null, "predicted": <number>, "confidence": <0-100> }
  ],
  "summary": "2 sentences describing the trend direction using only the provided numbers",
  "confidenceAvg": <average of the 3 confidence values>,
  "impact": "High" | "Medium" | "Low"
}
`;

  try {
    return await callGemini(prompt);
  } catch (err) {
    console.error("Gemini generatePredictions error:", err);
    return null;
  }
};

/**
 * Generate an AI report narrative.
 * Returns: { title, summary, keyFindings, recommendations, riskAreas }
 */
export const generateAIReport = async (reportType, data) => {
  const prompt = `
${STRICT_RULE}

You are a school report writer. Generate a ${reportType} based only on this data.

Data:
${JSON.stringify(data, null, 2)}

Return JSON:
{
  "title": "Report title",
  "summary": "3-4 sentences using only numbers from the data. No currency symbols.",
  "keyFindings": ["Finding using data value", "Finding using data value", "Finding using data value"],
  "recommendations": ["Action based on data", "Action based on data"],
  "riskAreas": ["Risk from data", "Risk from data"]
}
`;

  try {
    return await callGemini(prompt);
  } catch (err) {
    console.error("Gemini generateAIReport error:", err);
    return null;
  }
};
